import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const RoleSuccessRateChart = ({ data, description }) => {
  const roleSuccessData = () => {
    const roleStats = {};
    
    data.forEach(candidate => {
      // Use the role field which prioritizes "Which role was the offer for?" columns
      const role = candidate.role || 'Unknown';
      
      if (!roleStats[role]) {
        roleStats[role] = { total: 0, hired: 0, rejected: 0 };
      }
      roleStats[role].total++;
      if (candidate.stage === 'Hired') roleStats[role].hired++;
      if (candidate.stage === 'Rejected') roleStats[role].rejected++;
    });
    
    return Object.entries(roleStats)
      .map(([role, stats]) => ({
        role: role.length > 20 ? role.substring(0, 20) + '...' : role,
        successRate: stats.total > 0 ? Math.round((stats.hired / stats.total) * 100) : 0,
        hired: stats.hired,
        total: stats.total
      }))
      .filter(item => item.total >= 3) // Only show roles with at least 3 candidates
      .sort((a, b) => b.successRate - a.successRate)
      .slice(0, 8); // Show top 8 roles
  };

  const chartData = roleSuccessData();
  const avgSuccessRate = chartData.length > 0 ? 
    Math.round(chartData.reduce((sum, item) => sum + item.successRate, 0) / chartData.length) : 0;

  return (
    <div className="dashboard-card p-6 slide-in-up" style={{animationDelay: '1000ms'}}>
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1">Role Success Rates</h3>
          <p className="text-sm text-gray-500">Hiring success rate by role (min. 3 candidates)</p>
        </div>
        <div className="text-right">
          <div className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-1 rounded-full mb-1">
            Performance
          </div>
          <p className="text-xs text-gray-500">{avgSuccessRate}% avg success</p>
        </div>
      </div>
      {description && (
        <p className="text-sm text-gray-600 mb-4">{description}</p>
      )}
      <div className="bg-gray-50 rounded-lg p-4">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} margin={{ top: 20, right: 20, left: 20, bottom: 80 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.6} />
            <XAxis 
              dataKey="role" 
              tickLine={false} 
              axisLine={false} 
              style={{ fontSize: '10px', fill: '#6b7280' }} 
              angle={-45}
              textAnchor="end"
              height={70}
              interval={0}
            />
            <YAxis 
              tickLine={false} 
              axisLine={false} 
              style={{ fontSize: '11px', fill: '#6b7280' }}
              tickFormatter={(value) => `${value}%`}
              domain={[0, 100]}
              label={{ value: 'Success Rate (%)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: '11px', fill: '#6b7280' } }}
            />
            <Tooltip 
              contentStyle={{ 
                borderRadius: '12px', 
                border: 'none', 
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                fontSize: '12px',
                backgroundColor: 'white',
                padding: '12px'
              }}
              labelStyle={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}
              formatter={(value, name) => [
                `${value}%`,
                'Success Rate'
              ]}
              labelFormatter={(label) => `${label}`}
            />
            <Bar 
              dataKey="successRate" 
              fill="#22c55e" 
              name="Success Rate"
              radius={[4, 4, 0, 0]}
              maxBarSize={40}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RoleSuccessRateChart;